import React, { useEffect, useRef, useState } from "react";
import type { PostureAnalysis, CaptureSequence, Landmark } from "@/lib/types";
import {
  drawCustomPelvis,
  drawLandmarks,
  drawPlumb,
  drawViewportOrientationLabels,
  drawAP,
  drawLat,
  drawIdealGrid,
} from "@/components/posture-analysis/lib/drawing";
import { loadCaptureSettings } from "@/components/posture-analysis/lib/capture-settings";
import { FullScreenImageDialog } from "@/components/ui/full-screen-image-dialog";
import { usePostureMode } from '@/components/posture-analysis/lib/PostureModeContext';
import { shouldShowPelvisOverlay } from '@/components/posture-analysis/lib/posture-mode-strategy';
import { landmarksFitImage } from '@/components/posture-analysis/lib/scale-captured-spatial';
import { initPoseLandmarkerForImage, detectPoseInImage } from '@/lib/mediaPipeService';
import logger from '@/lib/logging';
import { Loader2 } from 'lucide-react';

type View = CaptureSequence;

export interface AnnotatedImageProps {
  view: View;
  analysis: PostureAnalysis;
  onLandmarksDetected?: (view: View, landmarks: Landmark[], cmPerPx: number) => void;
}

export const AnnotatedImage = ({ view, analysis, onLandmarksDetected }: AnnotatedImageProps) => {
    const mode = usePostureMode();
    const { laymanMode } = mode;
    const showPelvisOverlay = shouldShowPelvisOverlay(mode);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [canvasDataUrl, setCanvasDataUrl] = useState<string | null>(null);
    const [detectedLandmarks, setDetectedLandmarks] = useState<Landmark[] | null>(null);
    const [isDetecting, setIsDetecting] = useState(false);
    /** Natural image size — used to reject historical landmarks not scaled with compression. */
    const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
    const capturedImage = analysis.captured[view];

    const storedLandmarks =
        capturedImage?.landmarks && Array.isArray(capturedImage.landmarks) && capturedImage.landmarks.length > 0
            ? capturedImage.landmarks
            : null;

    // Only trust stored landmarks once we can verify they share the displayed image's pixel space.
    const existingLandmarks =
        storedLandmarks && imageSize && landmarksFitImage(storedLandmarks, imageSize.width, imageSize.height)
            ? storedLandmarks
            : null;

    const needsFallbackDetection =
        !!capturedImage?.imageData &&
        !!imageSize &&
        !existingLandmarks &&
        !detectedLandmarks;

    const activeLandmarks = existingLandmarks || detectedLandmarks;

    useEffect(() => {
        setDetectedLandmarks(null);
        setIsDetecting(false);
        setImageSize(null);
        setCanvasDataUrl(null);
    }, [view, capturedImage?.imageData]);

    useEffect(() => {
        if (!needsFallbackDetection) return;

        let cancelled = false;
        setIsDetecting(true);

        async function runFallbackDetection() {
            try {
                const landmarker = await initPoseLandmarkerForImage();
                if (cancelled) return;
                const clientHeight = analysis.clientHeight || 175;
                const detection = await detectPoseInImage(landmarker, capturedImage!.imageData!, clientHeight, { view });
                if (!cancelled && detection.landmarks && detection.landmarks.length > 0) {
                    setDetectedLandmarks(detection.landmarks);
                    onLandmarksDetected?.(view, detection.landmarks, detection.cmPerPx || 0.2);
                }
            } catch (err) {
                logger.warn(`[AnnotatedImage] Fallback landmark detection failed for view ${view}:`, err);
            } finally {
                if (!cancelled) {
                    setIsDetecting(false);
                }
            }
        }

        runFallbackDetection();

        return () => {
            cancelled = true;
        };
        // Do not depend on isDetecting — that cancelled in-flight work on the setState it triggered.
    }, [needsFallbackDetection, capturedImage, view, analysis.clientHeight, onLandmarksDetected]);

    useEffect(() => {
        if (!canvasRef.current || !capturedImage?.imageData) return;

        let cancelled = false;
        const img = new window.Image();
        img.onload = () => {
            if (cancelled || !canvasRef.current) return;

            setImageSize((prev) =>
                prev && prev.width === img.width && prev.height === img.height
                    ? prev
                    : { width: img.width, height: img.height },
            );

            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const maxDisplayWidth = 1200;
            const scale = Math.min(1, maxDisplayWidth / img.width);
            const newWidth = Math.round(img.width * scale);
            const newHeight = Math.round(img.height * scale);

            canvas.width = newWidth;
            canvas.height = newHeight;

            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0);

            const cap = loadCaptureSettings();
            const landmarks = activeLandmarks;
            if (landmarks && Array.isArray(landmarks)) {
                drawIdealGrid(
                    ctx,
                    view,
                    landmarks,
                    cap,
                    capturedImage.floorCorrectionDeg ?? 0,
                    0,
                    true,
                    'top',
                    false
                );
                if (view === 'Anterior' || view === 'Posterior') {
                    drawAP(ctx, landmarks, cap, null);
                } else {
                    drawLat(ctx, landmarks, view, capturedImage.custom || {}, cap, null);
                }
            }
            drawCustomPelvis(ctx, view, capturedImage.custom || {}, {
                landmarkSize: cap.landmarkSize,
                skeletonLineWidth: cap.skeletonLineWidth,
                customLandmarkColor: cap.customLandmarkColor,
                pelvisLineColor: cap.pelvisLineColor,
                pelvisLineWidth: cap.pelvisLineWidth,
                showPelvisLine: showPelvisOverlay && cap.showPelvisLine,
                showPelvisLandmarks: showPelvisOverlay && cap.showPelvisLandmarks,
            });
            drawViewportOrientationLabels(ctx, view, { width: img.width, height: img.height }, false);

            if (!cancelled) setCanvasDataUrl(canvas.toDataURL('image/png'));
        };
        img.src = capturedImage.imageData;

        return () => {
            cancelled = true;
        };
    }, [analysis, view, capturedImage, activeLandmarks, laymanMode, showPelvisOverlay]);

    if (!capturedImage?.imageData) {
        return (
            <div className="my-4 p-4 bg-muted rounded-md flex items-center justify-center h-full min-h-[200px]">
                <p className="text-muted-foreground text-sm">Image not available for this view.</p>
            </div>
        );
    }

    return (
        <FullScreenImageDialog imageUrl={canvasDataUrl || undefined} altText={`${view} Posture Analysis`}>
            <div className="relative inline-block w-full max-w-[350px]">
                <canvas ref={canvasRef} className="rounded-md w-full h-auto object-contain cursor-pointer" style={{ display: 'block', maxWidth: '350px' }} />
                {isDetecting && (
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] rounded-md flex flex-col items-center justify-center text-white text-xs font-medium gap-1.5 p-2 transition-all">
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        <span>Detecting landmarks...</span>
                    </div>
                )}
            </div>
        </FullScreenImageDialog>
    );
};
